import React, { useState } from 'react';
import moment from 'moment';
import { Form, Input, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
// Types
import { GetCampaignsV2_getCampaigns_entities } from 'api/campaignV2/types/GetCampaignsV2';
import { CampaignType } from 'api/graphql-global-types';
// Helpers
import { formatDaysToWeeks } from 'helpers/campaign';
// Constants
import { onlyDateTimeFormat, CampaignVersion } from 'constants/global';
// Components
import FormSection from '../FormSection/FormSection';
// Style
import styles from '../CampaignEditComponents.module.scss';

const { TextArea } = Input;

type CampaignDetailsProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  renderCustomFields?: () => React.ReactNode;
  showInstructions?: boolean;
};

const CampaignDetails = ({
  record,
  renderCustomFields,
  showInstructions = true,
}: CampaignDetailsProps): JSX.Element => {
  const campaignType = record?.type;
  const [currentApplicationDeadlineDays, setCurrentApplicationDeadlineDays] =
    useState<number | null>(record?.applicationDeadlineDays || null);

  // Only v1 Social campaigns use dueDate (legacy behavior).
  // All other campaigns (v2/v3 Social, and all versions of other types) use applicationDeadlineDays.
  const isV1SocialCampaign =
    record?.v === CampaignVersion.V1 && campaignType === CampaignType.Social;
  const showDueDateField = isV1SocialCampaign && record?.dueDate;
  // Social Boost has no application deadline concept (not collected on creation in CB).
  const showApplicationDeadlineDaysField =
    !isV1SocialCampaign && campaignType !== CampaignType.SocialBoost;
  // const [applicationDeadlineEnabled, setApplicationDeadlineEnabled] =
  //   useState<boolean>(record?.applicationDeadlineEnabled ?? true);

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // allow days 2 weeks from today
    return current && current < moment().add(2, 'weeks');
  };

  // Update currentApplicationDeadlineDays when form changes
  const handleApplicationDeadlineDaysChange = (value: number | null) => {
    setCurrentApplicationDeadlineDays(
      value || record?.applicationDeadlineDays || null
    );
  };

  // const handleApplicationDeadlineEnabledChange = (checked: boolean) => {
  //   setApplicationDeadlineEnabled(checked);
  // };

  return (
    <FormSection
      title="Campaign details"
      hint="How the campaign is named and how long talent have to apply."
    >
      <div className={styles.rowWrapper}>
        <Form.Item
          name={
            campaignType === CampaignType.SocialShare ||
            campaignType === CampaignType.SocialBoost
              ? 'campaignName'
              : 'name'
          }
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
          label="External Campaign Name"
          extra="The name talent see."
        >
          <Input
            type="name"
            placeholder="Enter External Campaign Name"
            aria-label="External Campaign Name"
          />
        </Form.Item>
        <Form.Item
          name="internalName"
          label="Internal Campaign Name"
          extra="Only shown in the backoffice."
        >
          <Input
            placeholder="Enter Internal Campaign Name"
            aria-label="Internal Campaign Name"
          />
        </Form.Item>
        {showDueDateField && (
          <Form.Item
            name="dueDate"
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
              {
                validator: (_, value) => {
                  if (value && moment().add(2, 'weeks').isAfter(value)) {
                    return Promise.reject(
                      new Error(
                        'Application deadline must be at least 2 weeks in the future.'
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            label="Application deadline date"
          >
            <DatePicker
              name="dueDate"
              format={onlyDateTimeFormat}
              disabledDate={disabledDate}
            />
          </Form.Item>
        )}
      </div>

      <div className={styles.rowWrapper}>
        {/* Maybe it would be added later, so just commented out for now */}
        {/* {record?.applicationDeadlineDays && (
          <Form.Item
            name="applicationDeadlineEnabled"
            label={<Text>Deadline Enabled</Text>}
            valuePropName="checked"
            style={{ marginBottom: '4px' }}
          >
            <Checkbox
              checked={applicationDeadlineEnabled}
              onChange={(e) =>
                handleApplicationDeadlineEnabledChange(e.target.checked)
              }
            />
          </Form.Item>
        )} */}
        {showApplicationDeadlineDaysField && (
          <Form.Item
            name="applicationDeadlineDays"
            getValueFromEvent={(e) => e.target.valueAsNumber || null}
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
              {
                validator: (_, value) => {
                  if (value && (value < 1 || value > 365)) {
                    return Promise.reject(
                      new Error(
                        'Application deadline must be between 1 and 365 days.'
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            label="Application deadline (days)"
            extra={
              currentApplicationDeadlineDays
                ? `${formatDaysToWeeks(
                    currentApplicationDeadlineDays
                  )} from the day the campaign goes live.`
                : 'Counted from the day the campaign goes live.'
            }
          >
            <Input
              type="number"
              step="1"
              placeholder="Enter number of days"
              min={1}
              max={365}
              aria-label="Application deadline days"
              onChange={(e) =>
                handleApplicationDeadlineDaysChange(
                  e.target.valueAsNumber || null
                )
              }
              // disabled={!applicationDeadlineEnabled}
            />
          </Form.Item>
        )}
      </div>

      {showInstructions && (
        <div className={styles.spanAll}>
          <Form.Item
            name="instructions"
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
            label="Campaign instructions"
          >
            <TextArea
              autoSize={{ minRows: 3 }}
              name="instructions"
              placeholder="Quick outline of key messaging and conversation points. What content do we want to capture to make the stream a hit."
            />
          </Form.Item>
        </div>
      )}

      {renderCustomFields?.()}
    </FormSection>
  );
};

export default CampaignDetails;
